import React, { useState } from 'react';
import { Form, Button, Modal, ListGroup } from 'react-bootstrap';
import Flags from "react-world-flags";
import { FaTrashAlt } from "react-icons/fa";
import GeneralSettingsService from '../../services/GeneralSettingsService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const EditForm = ({ userName, initialData, onCancel, onDataReload }) => {
    const [formValidated, setFormValidated] = useState(false);
    const [paramRates, setParamRates] = useState(
        initialData.paramRates?.map(rate => ({
            ...rate,
            withDrawalMethods: rate.withDrawalMethods ? rate.withDrawalMethods.split(', ') : []
        })) || []
    );
    const [emailInterac, setEmailInterac] = useState(initialData.emailInterac || '');
    const [emailNotification, setEmailNotification] = useState(initialData.emailNotification || '');
    const [bankDepositMinAmount, setBankDepositMinAmount] = useState(initialData.bankDepositMinAmount || 0);
    const [bankDepositMaxAmount, setBankDepositMaxAmount] = useState(initialData.bankDepositMaxAmount || 0);
    const [walletDepositMinAmount, setWalletDepositMinAmount] = useState(initialData.walletDepositMinAmount || 0);
    const [walletDepositMaxAmount, setWalletDepositMaxAmount] = useState(initialData.walletDepositMaxAmount || 0);
    const [interacName, setInteracName] = useState(initialData.interacName || '');
    const [securityQuestion, setSecurityQuestion] = useState(initialData.securityQuestion || '');
    const [securityAnswer, setSecurityAnswer] = useState(initialData.securityAnswer || '');
    const [isEditing, setIsEditing] = useState(false);

    // Ajout de WAVE comme méthode de retrait
    const withdrawalMethods = ["BANK", "ORANGE_MONEY", "MTN_MOBILE_MONEY", "WAVE"];

    // Ajout du Sénégal comme pays
    const countries = [
        { code: "CM", name: "Cameroun" },
        { code: "CI", name: "Côte d'Ivoire" },
        { code: "BJ", name: "Bénin" },
        { code: "BF", name: "Burkina Faso" },
        { code: "SN", name: "Sénégal" }  // Ajout du Sénégal
    ];

    const handleAddParamRate = () => {
        setParamRates([
            ...paramRates,
            {
                country: '',
                rate: '',
                withDrawalMethods: [],
                userName: userName,
            },
        ]);
    };

    const handleParamRateChange = (index, field, value) => {
        const updatedParamRates = [...paramRates];
        updatedParamRates[index][field] = value;
        setParamRates(updatedParamRates);
    };

    const handleWithdrawalMethodSelect = (index, e) => {
        const method = e.target.value;
        if (method && !paramRates[index].withDrawalMethods.includes(method)) {
            const updatedParamRates = [...paramRates];
            updatedParamRates[index].withDrawalMethods.push(method);
            setParamRates(updatedParamRates);
            e.target.value = '';
        }
    };

    const handleWithdrawalMethodRemove = (index, method) => {
        const updatedParamRates = [...paramRates];
        updatedParamRates[index].withDrawalMethods = updatedParamRates[index].withDrawalMethods.filter(m => m !== method);
        setParamRates(updatedParamRates);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        if (form.checkValidity() === false) {
            event.stopPropagation();
        }
        setFormValidated(true);

        const formData = {
            id: initialData.id,
            emailInterac,
            emailNotification,
            bankDepositMinAmount,
            bankDepositMaxAmount,
            walletDepositMinAmount,
            walletDepositMaxAmount,
            interacName,
            securityQuestion,
            securityAnswer,
            userName,
            paramRates: paramRates.map(paramRate => ({
                ...paramRate,
                withDrawalMethods: paramRate.withDrawalMethods.join(', ')
            }))
        };

        try {
            await GeneralSettingsService.updateParameter(formData);
            toast.success('Paramètres mis à jour avec succès!');
            setIsEditing(false);
            onDataReload();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour des paramètres.');
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
    };

    return (
        <Form style={{paddingBottom: '25px'}} noValidate validated={formValidated} onSubmit={handleSubmit}>
            <Modal.Body>
                {!isEditing && (
                    <Button variant="secondary" onClick={handleEdit} className="mt-3">
                        Modifier
                    </Button>
                )}

                <Form.Group controlId="emailInterac" className="form-floating mt-3">
                    <Form.Control
                        type="email"
                        value={emailInterac}
                        onChange={(e) => setEmailInterac(e.target.value)}
                        required
                        disabled={!isEditing}
                    />
                    <Form.Label>Email Interac</Form.Label>
                    <Form.Control.Feedback type="invalid">
                        Veuillez entrer un email valide.
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="emailNotification" className="form-floating mt-3">
                    <Form.Control
                        type="email"
                        value={emailNotification}
                        onChange={(e) => setEmailNotification(e.target.value)}
                        required
                        disabled={!isEditing}
                    />
                    <Form.Label>Email Notification</Form.Label>
                    <Form.Control.Feedback type="invalid">
                        Veuillez entrer un email valide.
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="bankDepositMinAmount" className="form-floating mt-3">
                    <Form.Control
                        type="number"
                        value={bankDepositMinAmount}
                        onChange={(e) => setBankDepositMinAmount(e.target.value)}
                        required
                        disabled={!isEditing}
                    />
                    <Form.Label>Montant Minimum Dépôt Bancaire</Form.Label>
                </Form.Group>

                <Form.Group controlId="bankDepositMaxAmount" className="form-floating mt-3">
                    <Form.Control
                        type="number"
                        value={bankDepositMaxAmount}
                        onChange={(e) => setBankDepositMaxAmount(e.target.value)}
                        required
                        disabled={!isEditing}
                    />
                    <Form.Label>Montant Maximum Dépôt Bancaire</Form.Label>
                </Form.Group>

                <Form.Group controlId="walletDepositMinAmount" className="form-floating mt-3">
                    <Form.Control
                        type="number"
                        value={walletDepositMinAmount}
                        onChange={(e) => setWalletDepositMinAmount(e.target.value)}
                        required
                        disabled={!isEditing}
                    />
                    <Form.Label>Montant Minimum Dépôt Portefeuille</Form.Label>
                </Form.Group>

                <Form.Group controlId="walletDepositMaxAmount" className="form-floating mt-3">
                    <Form.Control
                        type="number"
                        value={walletDepositMaxAmount}
                        onChange={(e) => setWalletDepositMaxAmount(e.target.value)}
                        required
                        disabled={!isEditing}
                    />
                    <Form.Label>Montant Maximum Dépôt Portefeuille</Form.Label>
                </Form.Group>

                <Form.Group controlId="interacName" className="form-floating mt-3">
                    <Form.Control
                        type="text"
                        value={interacName}
                        onChange={(e) => setInteracName(e.target.value)}
                        required
                        disabled={!isEditing}
                    />
                    <Form.Label>Nom Interac</Form.Label>
                </Form.Group>

                <Form.Group controlId="securityQuestion" className="form-floating mt-3">
                    <Form.Control
                        type="text"
                        value={securityQuestion}
                        onChange={(e) => setSecurityQuestion(e.target.value)}
                        required
                        disabled={!isEditing}
                    />
                    <Form.Label>Question de Sécurité</Form.Label>
                </Form.Group>

                <Form.Group controlId="securityAnswer" className="form-floating mt-3">
                    <Form.Control
                        type="text"
                        value={securityAnswer}
                        onChange={(e) => setSecurityAnswer(e.target.value)}
                        required
                        disabled={!isEditing}
                    />
                    <Form.Label>Réponse de Sécurité</Form.Label>
                </Form.Group>

                <div className="mt-4 mb-4 d-flex align-items-center">
                    <div className="flex-grow-1 border-bottom"></div>
                    <h5 className="mx-3 mb-0">Paramètres de Taux</h5>
                    <div className="flex-grow-1 border-bottom"></div>
                </div>

                {paramRates.map((paramRate, index) => {
                    const selectedCountry = countries.find(country => country.name === paramRate.country);
                    return (
                        <div key={index} className="mt-3 border p-3 rounded">
                            <Form.Group controlId={`country-${index}`} className="form-floating">
                                <Form.Select
                                    value={paramRate.country}
                                    onChange={(e) => handleParamRateChange(index, 'country', e.target.value)}
                                    required
                                    disabled={!isEditing}
                                >
                                    <option value="">Sélectionnez un pays</option>
                                    {countries.map((country, i) => (
                                        <option key={i} value={country.name}>
                                            {country.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Label>
                                    {selectedCountry ? (
                                        <div className="d-flex align-items-center">
                                            <Flags
                                                code={selectedCountry.code}
                                                style={{ width: "20px", marginRight: "8px" }}
                                            />
                                            Pays
                                        </div>
                                    ) : 'Pays'}
                                </Form.Label>
                                <Form.Control.Feedback type="invalid">
                                    Veuillez sélectionner un pays.
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId={`rate-${index}`} className="form-floating mt-3">
                                <Form.Control
                                    type="number"
                                    value={paramRate.rate}
                                    onChange={(e) => handleParamRateChange(index, 'rate', e.target.value)}
                                    required
                                    disabled={!isEditing}
                                />
                                <Form.Label>Taux</Form.Label>
                                <Form.Control.Feedback type="invalid">
                                    Veuillez entrer un taux valide.
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId={`withDrawalMethods-${index}`} className="mt-3">
                                <Form.Label>Méthodes de Retrait</Form.Label>
                                <Form.Select
                                    onChange={(e) => handleWithdrawalMethodSelect(index, e)}
                                    value=""
                                    disabled={!isEditing}
                                >
                                    <option value="">Sélectionner une méthode</option>
                                    {withdrawalMethods
                                        .filter(method => !paramRate.withDrawalMethods.includes(method))
                                        .map((method, i) => (
                                            <option key={i} value={method}>
                                                {method}
                                            </option>
                                        ))
                                    }
                                </Form.Select>

                                <ListGroup className="mt-2">
                                    {paramRate.withDrawalMethods.map((method, i) => (
                                        <ListGroup.Item key={i} className="d-flex justify-content-between align-items-center">
                                            {method}
                                            {isEditing && (
                                                <Button
                                                    variant="link"
                                                    className="p-0 text-danger"
                                                    onClick={() => handleWithdrawalMethodRemove(index, method)}
                                                >
                                                    <FaTrashAlt />
                                                </Button>
                                            )}
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            </Form.Group>
                        </div>
                    );
                })}

                {/* {isEditing && (
                    <Button variant="outline-primary" onClick={handleAddParamRate} className="mt-3">
                        Ajouter un taux
                    </Button>
                )} */}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-end gap-2" style={{paddingBottom: '10px', paddingTop: '15px'}}>
                {isEditing && (
                    <>
                        <Button variant="secondary" onClick={handleCancelEdit}>
                            Annuler
                        </Button>
                        <Button variant="primary" type="submit">
                            Sauvegarder
                        </Button>
                    </>
                )}
            </Modal.Footer>
            <ToastContainer />
        </Form>
    );
};

export default EditForm;